import { Play } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { RunControl } from "~/api/client";
import { useControlRun, useDispatches, useRuns } from "~/api/queries";
import { ABILITY, type Run } from "~/api/types";
import { Pill, Row, Rows, StatusPill } from "~/features/automation/components";
import { formatDuration } from "~/features/automation/format";
import { RunAnalyticsBand } from "~/features/automation/RunAnalyticsBand";
import { runKindLabel } from "~/features/automation/status";
import { Select } from "~/features/settings/controls";
import { Button } from "~/foundation/components/Button";
import { Empty } from "~/foundation/components/Empty";
import { Input } from "~/foundation/components/Input";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { cn } from "~/foundation/utils/cn";
import { formatRelativeTime } from "~/foundation/utils/time";

const RUN_STATUSES = [
  "requested",
  "running",
  "observing",
  "paused",
  "completed",
  "completed-degraded",
  "failed",
  "cancelled",
];

const RUN_KINDS = ["digest", "flow", "pulse-advisor"];

const CONTROLS: Array<{ action: RunControl; ability: string; label: string }> = [
  { action: "pause", ability: ABILITY.runPause, label: "Pause" },
  { action: "resume", ability: ABILITY.runResume, label: "Resume" },
  { action: "cancel", ability: ABILITY.runCancel, label: "Cancel" },
  { action: "retry", ability: ABILITY.runRetry, label: "Run again" },
];

/**
 * The control strip. Every verb here is offered because the *server* said it has an executor
 * to reach — never because of a role, and never because the status looked right. A button
 * that reaches nobody is worse than no button: it reports success while the run keeps going.
 */
function RunControls({ run }: { run: Run }) {
  const { t } = useTranslation();
  const control = useControlRun();
  const available = CONTROLS.filter((entry) => run.abilities[entry.ability] === true);

  if (available.length === 0) {
    return null;
  }

  return (
    <div className="flex shrink-0 items-center gap-1">
      {available.map((entry) => (
        <Button
          key={entry.action}
          size="sm"
          variant={entry.action === "cancel" ? "ghost" : "neutral"}
          disabled={control.isPending}
          onClick={(event) => {
            // Row is a Link — the control buttons must not also navigate.
            event.preventDefault();
            event.stopPropagation();
            control.mutate({ id: run.id, action: entry.action });
          }}
        >
          {t(entry.label)}
        </Button>
      ))}
    </div>
  );
}

function Dispatches() {
  const { t, i18n } = useTranslation();
  const { data: dispatches, isLoading } = useDispatches();

  if (isLoading) {
    return <Spinner label={t("Loading")} />;
  }
  if (!dispatches?.length) {
    return <Empty message={t("Nothing has been handed to an editor window.")} />;
  }

  return (
    <div className="flex flex-col gap-2">
      <Text variant="small">
        {t("Work queued for an editor window to claim. Unclaimed work expires.")}
      </Text>
      <Rows>
        {dispatches.map((dispatch) => (
          <Row key={dispatch.id}>
            <div className="min-w-0 flex-1">
              <p className="truncate text-body text-text">
                {dispatch.redacted ? t("Someone else’s work") : (dispatch.result ?? dispatch.kind)}
              </p>
              <p className="truncate text-small text-text-tertiary">
                {dispatch.claimedBy ?? dispatch.target ?? t("Any window")}
              </p>
            </div>
            <Pill>{dispatch.agent}</Pill>
            <StatusPill status={dispatch.status} />
            <span className="hidden w-24 text-small text-text-tertiary sm:inline">
              {formatRelativeTime(dispatch.createdAt, i18n.language)}
            </span>
          </Row>
        ))}
      </Rows>
    </div>
  );
}

/**
 * What has executed, what it cost and what it produced. A row click opens the full viewer at
 * `/runs/$runId` — a route, not a modal, because the viewer hosts a full-height canvas and a
 * navigation back-stack for child runs (§D.1).
 */
export function Runs() {
  const { t, i18n } = useTranslation();
  const [view, setView] = useState<"runs" | "dispatches">("runs");
  const [status, setStatus] = useState("");
  const [kind, setKind] = useState("");
  const [search, setSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);

  const { data: runs, isLoading } = useRuns({
    ...(status ? { status } : {}),
    ...(kind ? { kind } : {}),
    ...(activeOnly ? { activeOnly } : {}),
    limit: 200,
  });

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) {
      return runs ?? [];
    }
    return (runs ?? []).filter((run) => run.name.toLowerCase().includes(needle));
  }, [runs, search]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-1 border-b border-divider">
        {(["runs", "dispatches"] as const).map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setView(value)}
            className={cn(
              "cursor-pointer border-b-2 px-2 pb-1.5 text-body",
              view === value
                ? "border-accent text-text"
                : "border-transparent text-text-secondary hover:text-text",
            )}
          >
            {value === "runs" ? t("Runs") : t("Handed off")}
          </button>
        ))}
      </div>

      {view === "dispatches" ? (
        <Dispatches />
      ) : (
        <>
          <RunAnalyticsBand />

          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={kind}
              aria-label={t("Filter by kind")}
              onChange={(event) => setKind(event.target.value)}
            >
              <option value="">{t("All kinds")}</option>
              {RUN_KINDS.map((value) => (
                <option key={value} value={value}>
                  {runKindLabel(value, t)}
                </option>
              ))}
            </Select>
            <Select
              value={status}
              aria-label={t("Filter by status")}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="">{t("Any status")}</option>
              {RUN_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {value === "completed-degraded" ? t("completed (degraded)") : value}
                </option>
              ))}
            </Select>
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("Filter by name…")}
              aria-label={t("Search runs by name")}
              className="min-w-48"
            />
            <Button
              variant={activeOnly ? "primary" : "neutral"}
              size="sm"
              onClick={() => setActiveOnly((previous) => !previous)}
            >
              {t("Only what is still running")}
            </Button>
          </div>

          {isLoading ? (
            <Spinner label={t("Loading")} />
          ) : filtered.length ? (
            <Rows>
              {filtered.map((run) => (
                <Row key={run.id} className="p-0">
                  <Link
                    to="/runs/$runId"
                    params={{ runId: run.id }}
                    className="flex min-w-0 flex-1 items-center gap-3 px-3 py-2.5 text-start hover:bg-hover-bg"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-body text-text">{run.name}</p>
                      <p className="truncate text-small text-text-tertiary">
                        {t("{{done}} of {{total}} steps", {
                          done: run.steps.completed,
                          total: run.steps.total,
                        })}
                        {run.steps.failed > 0
                          ? ` · ${t("{{count}} failed", { count: run.steps.failed })}`
                          : ""}
                      </p>
                    </div>
                    {run.awaitingApprovalNodeId ? (
                      <Pill tone="warning">{t("Waiting for you")}</Pill>
                    ) : null}
                    {run.degradedSources > 0 ? (
                      <span
                        title={t("{{count}} declared source(s) did not resolve", {
                          count: run.degradedSources,
                        })}
                      >
                        <Pill tone="warning">{run.degradedSources}</Pill>
                      </span>
                    ) : null}
                    <StatusPill status={run.status} />
                    <span className="hidden w-16 text-small text-text-tertiary tabular-nums sm:inline">
                      {formatDuration(run.durationMs, t)}
                    </span>
                    <span className="hidden w-24 text-small text-text-tertiary md:inline">
                      {formatRelativeTime(run.createdAt, i18n.language)}
                    </span>
                    <RunControls run={run} />
                  </Link>
                </Row>
              ))}
            </Rows>
          ) : (
            <Empty icon={Play} message={t("Nothing has run yet.")} />
          )}
        </>
      )}
    </div>
  );
}
